import { cn } from '@client/utils/styling/cn';
import { createLink, type LinkComponent } from '@tanstack/react-router';
import { RefreshCw } from 'lucide-react';
import { forwardRef, useEffect, useMemo, useRef, useState } from 'react';
import { ButtonIcon } from './ButtonIcon';
import { LinkTooltip } from './LinkTooltip';
import type { IconButtonLinkProps, RefreshButtonProps } from './types';

const sizeClasses = {
    micro: 'btn-micro',
    small: 'btn-sm',
    middle: 'btn-md',
    large: 'btn-lg',
} as const;

const iconSizes = {
    micro: 12,
    small: 14,
    middle: 16,
    large: 20,
} as const;

/** Must match Tailwind's `animate-spin` (`spin 1s linear infinite`). */
const SPIN_PERIOD_MS = 1000;

/**
 * Spin in whole turns: starts with `loading`, but once it clears the animation runs on to the
 * end of the current rotation — a sub-second refetch still shows one full, legible spin and the
 * icon never snaps back from mid-turn.
 */
function useWholeTurnSpin(loading: boolean): boolean {
    const [spinning, setSpinning] = useState(false);
    const startedAt = useRef(0);

    useEffect(() => {
        if (loading) {
            if (!startedAt.current) startedAt.current = Date.now();
            setSpinning(true);
            return;
        }
        if (!startedAt.current) return;
        const elapsed = Date.now() - startedAt.current;
        const timer = setTimeout(
            () => {
                startedAt.current = 0;
                setSpinning(false);
            },
            SPIN_PERIOD_MS - (elapsed % SPIN_PERIOD_MS),
        );
        return () => clearTimeout(timer);
    }, [loading]);

    return spinning;
}

/**
 * Standardised refresh button with loading state
 *
 * Uses accent colour by default, customisable via color prop. While loading, the refresh icon
 * itself spins (rather than swapping to the generic loader) and stays lit.
 *
 * @example
 * // Button mode - for triggering refetch
 * <Button.Refresh onClick={refetch} loading={isRefetching} />
 *
 * @example
 * <Button.Refresh
 *   onClick={handleRefresh}
 *   tooltip="Refresh data"
 *   loading={isPending}
 * />
 *
 * @example
 * // With custom colour
 * <Button.Refresh onClick={handleRefresh} color="cyan" />
 */
export function ButtonRefresh({
    onClick,
    disabled,
    loading = false,
    tooltip = 'Refresh',
    size = 'middle',
    color = 'accent',
}: RefreshButtonProps) {
    const spinning = useWholeTurnSpin(loading);
    return (
        <ButtonIcon
            icon={<RefreshCw className={spinning ? 'animate-spin' : undefined} />}
            color={color}
            size={size}
            tooltip={tooltip}
            disabled={disabled || loading}
            onClick={onClick}
        />
    );
}

/**
 * Base anchor component for refresh links
 * Styled as an accent icon button using ButtonIcon CSS classes
 */
const ButtonRefreshLinkBase = forwardRef<HTMLAnchorElement, IconButtonLinkProps>(
    (
        { color = 'accent', size = 'middle', tooltip = 'Refresh', disabled, disabledReason, className, ...props },
        ref,
    ) => {
        const linkClasses = useMemo(
            () =>
                cn(
                    'btn-variant',
                    'btn-icon-colored',
                    `btn-color-${color}`,
                    sizeClasses[size],
                    disabled && 'pointer-events-none opacity-50',
                    className,
                ),
            [color, size, disabled, className],
        );

        const link = (
            <a
                ref={ref}
                aria-disabled={disabled}
                tabIndex={disabled ? -1 : undefined}
                // Icon-only link: the hover tooltip is not an accessible name, so a string tooltip doubles
                // as one (axe `link-name`). The label is set after the spread so a forwarded `undefined` cannot erase it.
                className={linkClasses}
                {...props}
                aria-label={props['aria-label'] ?? (typeof tooltip === 'string' ? tooltip : undefined)}
            >
                <span className="btn-icon">
                    <RefreshCw size={iconSizes[size]} />
                </span>
            </a>
        );

        return (
            <LinkTooltip disabled={disabled} disabledReason={disabledReason} tooltip={tooltip}>
                {link}
            </LinkTooltip>
        );
    },
);

ButtonRefreshLinkBase.displayName = 'ButtonRefreshLinkBase';

/**
 * Button.Refresh.Link - Type-safe router link styled as a refresh icon button
 *
 * Uses TanStack Router for type-safe `to`, `params`, and `search` props.
 * Accent colour by default, customisable via color prop.
 *
 * @example
 * // Navigate to refresh a page (force reload via router)
 * <Button.Refresh.Link
 *   to="/console/$orgId/claims"
 *   params={{ orgId: '1' }}
 *   tooltip="Go to claims"
 * />
 */
export const ButtonRefreshLink: LinkComponent<typeof ButtonRefreshLinkBase> = createLink(ButtonRefreshLinkBase);
